import { DOCUMENT_LIBRARY_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, Text } from '@mantine/core';
import { FindRelatedDocumentsRes } from '@dealstack/shared/lib/generated';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useRelatedToModal } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/RelatedToModal/useRelatedToModal';
import { TableArea } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/RelatedToModal/styled';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { DocumentsModal } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/components/DocumentsModal';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';

interface Props {
	isOpen: boolean;
	toggleModal: () => void;
	editDocument: EditDocument;
	dealId: string;
	onLinksSaved?: () => Promise<void> | void;
}

/**
 * Modal that will popup when there are editing the related to functionality
 */
export const RelatedToModal = ({
	isOpen,
	toggleModal,
	editDocument,
	dealId,
	onLinksSaved,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		onClose,
		mantineTableDataOperations,
		columns,
		relatedDocuments,
		onSubmit,
		isLoading,
		isError,
		isSubmitLoading,
	} = useRelatedToModal({
		toggleModal,
		editDocument,
		isOpen,
		dealId,
		onLinksSaved,
	});

	// DC-999: a failed fetch leaves the previous rows cached, and acting on them re-posts what the
	// user already saved. Say it failed rather than offering them.
	if (isError) {
		return (
			<DocumentsModal
				isOpen={isOpen}
				onClose={onClose}
				title={tCapManage('documents.errors.get-document')}
			>
				<EmptyState
					icon="es-open-box"
					title={tCapManage('documents.errors.get-document')}
				/>
			</DocumentsModal>
		);
	}

	// Only once the fetch has settled — otherwise every open flashes "no related documents".
	if (!isLoading && relatedDocuments.length === 0) {
		return (
			<DocumentsModal
				isOpen={isOpen}
				onClose={onClose}
				title={tCapManage('document.related_to.no_related_documents')}
			>
				<EmptyState
					icon="es-open-box"
					title={tCapManage('document.related_to.no_related_documents')}
				/>
			</DocumentsModal>
		);
	}

	return (
		<DocumentsModal
			isOpen={isOpen}
			onClose={onClose}
			title={tCapManage('document.related_to.header')}
			fillHeight={true}
		>
			<Text size="md" ta="center" mb="md">
				{tCapManage('document.related_to.sub_header')}
			</Text>
			<TableArea>
				<DocumentTable<FindRelatedDocumentsRes>
					enableRowSelection={false}
					enableExpandAll={true}
					enablePagination={false}
					enableRowActions={false}
					dataOperations={mantineTableDataOperations}
					enableTopToolbar={false}
					columns={columns}
					isLoading={isLoading}
					data={relatedDocuments}
					rowCount={relatedDocuments.length}
				/>
			</TableArea>
			<Flex gap="1rem" direction="row-reverse" pt="md">
				<Button
					data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.RELATED_TO_CANCEL_BUTTON}
					variant="secondary"
					onClick={onClose}
				>
					{tCapManage('document.related_to.buttons.cancel')}
				</Button>
				<Button
					data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.RELATED_TO_UPDATE_BUTTON}
					variant="primary"
					onClick={onSubmit}
					loading={isSubmitLoading}
				>
					{tCapManage('document.related_to.buttons.update')}
				</Button>
			</Flex>
		</DocumentsModal>
	);
};
